import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { listSchoolsDirect } from '@timetable/api';
import { SchoolList } from '../components/superadmin/SchoolList';
import { SchoolRegistrationDialog } from '../components/superadmin/SchoolRegistrationDialog';
import { queryKeys } from '../lib/queryKeys';
import type { School } from '@timetable/types';

export function SchoolManagementPage() {
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState<School | null>(null);

  // Fetch schools with caching (5 min stale time for performance)
  // Requirements: 4.1.1 - School list loads in < 1 second
  const {
    data: schools,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.schools.lists(),
    queryFn: listSchoolsDirect,
    staleTime: 5 * 60 * 1000, // 5 minutes - cache school list for performance
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
  });

  const handleEdit = (school: School) => {
    setEditingSchool(school);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            School Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Register and manage schools in the system
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setRegistrationDialogOpen(true)}
        >
          Register School
        </Button>
      </Stack>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load schools: {(error as Error).message}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* School List */}
      {!isLoading && (
        <SchoolList schools={schools} isLoading={isLoading} onEdit={handleEdit} />
      )}

      {/* Registration Dialog */}
      <SchoolRegistrationDialog
        open={registrationDialogOpen}
        onClose={() => setRegistrationDialogOpen(false)}
      />

      {/* Edit Dialog */}
      <SchoolRegistrationDialog
        open={!!editingSchool}
        onClose={() => setEditingSchool(null)}
        school={editingSchool}
      />
    </Box>
  );
}
